import BottomSheet, { BottomSheetBackdrop, useBottomSheetDynamicSnapPoints } from '@gorhom/bottom-sheet';
import { Pressable, SxProp, View } from 'dripsy';
import { t } from 'i18next';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { UniButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { LayoutContext } from 'contexts/LayoutContext';
import { DateRangeProps } from '@/design-system/components/dateRangePicker';
import { useDateRangePickerSelector } from '@/design-system/components/dateRangePicker/useDateRangePickerSelector';
import { BottomSheetHeader } from '@/design-system/components/headers';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { DATE_FORMAT, formatDate } from '@/utils/dateFormatting';
import { Calendar } from '@/design-system/components/dateRangePicker/Calendar';
import { Icon } from '@/design-system/components/icons/Icon';
import { Moment } from 'moment-timezone';
import { CardAccountStackRoutes, CardAccountStackScreenProps } from '../../card.navigator';

export interface BottomSheetDatePickerProps {
  onChange: (value: Moment | null) => void;
  title?: string;
  dateProps: Moment | null;
  options?: {
    minDate?: Moment | null;
    maxDate?: Moment | null;
    yearInputFlag?: boolean;
  };
}

export const BottomSheetDatePicker: React.FC<CardAccountStackScreenProps<CardAccountStackRoutes.BottomSheetDatePicker>> = ({
  route: {
    params: { title, onChange, dateProps, options }
  },
  navigation
}) => {
  const bottomSheetRef = useRef<BottomSheet>(null);
  const layout = useContext(LayoutContext);

  const selector = useDateRangePickerSelector({ onValueChange: () => {} });

  const [selectedDate, setSelectedDate] = useState<Moment | null>(dateProps);
  const onConfirm = () => {
    onChange(selectedDate);
  };

  const onBack = () => {
    bottomSheetRef.current?.close();
  };

  useEffect(() => {
    setSelectedDate(dateProps);
  }, [dateProps]);

  // Get dimensions
  const renderBackdrop = useCallback(
    (props) => <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />,
    []
  );
  const { width, height } = layout.dimensions;
  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);
  // Bottom Sheet on mobile
  return (
    <View sx={{ width, height, backgroundColor: 'transparent' }}>
      <BottomSheet
        enablePanDownToClose
        onClose={() => navigation.goBack()}
        index={0}
        ref={bottomSheetRef}
        snapPoints={animatedSnapPoints}
        handleHeight={animatedHandleHeight}
        contentHeight={animatedContentHeight}
        backdropComponent={renderBackdrop}
        handleComponent={() => <View sx={styles.bottomsheetHeadHandler} />}>
        <View sx={{ height: 'auto', width, backgroundColor: '$backgroundPrimary', paddingX: '$4' }} onLayout={handleContentLayout}>
          <BottomSheetHeader title={t('earn-invest::glossary.mobile-filter-date')} onPress={onBack} />
          <View sx={{ pt: '$4', pb: '$5' }}>
            <View sx={styles.dateButton}>
              <UniButton
                size="small"
                variant={!selectedDate ? ButtonVariant.outline1 : ButtonVariant.outline}
                label={
                  selectedDate
                    ? formatDate(selectedDate.toString(), { format: DATE_FORMAT.NO_TIME })
                    : t('global-bill-payment::glossary.select-date')
                }
                onPress={() => undefined}
                wrapperSx={styles.flex1}
              />
            </View>
            <View sx={{}}>
              <Calendar
                side="SINGLE"
                mode="DATE"
                selectedDateRange={{
                  from: null,
                  to: null
                }}
                selectedDate={selectedDate}
                calendar={selector.calendarLeft}
                onSelectDate={(date) => {
                  if (!date) return;
                  setSelectedDate(date);
                }}
                options={options}
                onPreviousMonth={selector.onPreviousMonth}
                onNextMonth={selector.onNextMonth}
                onPreviousYear={selector.onPreviousYear}
                onNextYear={selector.onNextYear}
                onYear={selector.onYear}
              />
            </View>
          </View>
          <View sx={{ mt: '$5', mb: '$4' }}>
            <UniButton
              size="default"
              label={t('earn-invest::glossary.done')}
              onPress={() => {
                onConfirm();
                onBack();
              }}
            />
          </View>
        </View>
      </BottomSheet>
    </View>
  );
};

enum StyleName {
  bottomsheetHeadHandler = 'bottomsheetHeadHandler',
  header = 'header',
  dateButton = 'dateButton',
  flex1 = 'flex1'
}

const styles: Record<StyleName, SxProp> = {
  bottomsheetHeadHandler: {
    height: '$5',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    backgroundColor: '$backgroundPrimary',
    borderWidth: 0
  },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  dateButton: {
    gap: '$3',
    mb: '$3',
    flexDirection: 'row',
    alignItems: 'center'
  },
  flex1: {
    flex: 1
  }
};
